import { useState } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Search, FileDown, Mail, Send, Loader2, Eye, Trash2, AlertTriangle, CheckCircle2, Copy, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCurrency, fmtDate, FENCE_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import ConfirmSendQuoteDialog from "@/components/ConfirmSendQuoteDialog";
import DuplicateQuoteDialog from "@/components/DuplicateQuoteDialog";

const relTime = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const sendQuote = async (q) => {
  // kept for compatibility — not used from the list UI anymore (goes via dialog)
  try {
    const { data } = await api.post(`/quotes/${q.id}/send`);
    if (data.mocked) toast.warning(`Email to ${data.email} logged only — add the Resend API key to send for real.`);
    else toast.success(`Quote PDF + portal link emailed to ${data.email}`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not send quote");
  }
};

const downloadPdf = async (q) => {
  try {
    const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CCFI-${q.quote_number || q.id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not generate PDF");
  }
};

const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  pending_approval: "bg-amber-100 text-amber-800 border-amber-300",
  changes_requested: "bg-rose-100 text-rose-800 border-rose-300",
  approved: "bg-cyan-100 text-cyan-800 border-cyan-300",
  sent: "bg-sky-100 text-sky-800 border-sky-300",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-300",
  declined: "bg-rose-100 text-rose-800 border-rose-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

// Display stage: workflow approval state folded into the lifecycle status.
const stageOf = (q) => {
  if (q.status === "accepted") return "accepted";
  if (q.status === "declined" || q.status === "rejected") return "declined";
  if (q.status === "sent") return "sent";
  const appr = q.approval_status || "draft";
  if (appr === "pending_approval") return "pending_approval";
  if (appr === "changes_requested") return "changes_requested";
  if (appr === "approved") return "approved";
  return "draft";
};

const STAGE_LABEL = {
  draft: "Draft", pending_approval: "Pending approval", changes_requested: "Changes requested",
  approved: "Approved", sent: "Sent", accepted: "Accepted", declined: "Declined",
};
const STAGES = ["draft", "pending_approval", "changes_requested", "approved", "sent", "accepted", "declined"];

const SENT_PERIODS = [
  { v: "week", label: "Sent — last week" },
  { v: "month", label: "Sent — last month" },
  { v: "quarter", label: "Sent — last quarter" },
  { v: "ytd", label: "Sent — year to date" },
];
const sentSince = (p) => {
  if (p === "ytd") return new Date(new Date().getFullYear(), 0, 1);
  const days = { week: 7, month: 30, quarter: 92 }[p];
  return new Date(Date.now() - days * 86400000);
};

// Highlights the matched search text within a cell value.
const hi = (text, needle) => {
  const s = text == null ? "" : String(text);
  if (!needle || !s) return s;
  const i = s.toLowerCase().indexOf(needle);
  if (i === -1) return s;
  return (<>{s.slice(0, i)}<mark className="bg-yellow-200 rounded-sm">{s.slice(i, i + needle.length)}</mark>{s.slice(i + needle.length)}</>);
};

export default function Quotes() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [confirmQ, setConfirmQ] = useState(null);
  const [sending, setSending] = useState(false);
  const [delQ, setDelQ] = useState(null);
  const [dupQ, setDupQ] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [sentFilter, setSentFilter] = useState("all");
  const [qSearch, setQSearch] = useState("");
  const { data: quotes = [] } = useQuery({
    queryKey: ["quotes"], queryFn: async () => (await api.get("/quotes")).data,
  });

  const needle = qSearch.trim().toLowerCase();
  const filtered = quotes.filter((q) => {
    if (needle) {
      const hay = [q.quote_number, q.customer_name, q.job_number, FENCE_LABELS[q.fence_type] || q.fence_type, STAGE_LABEL[stageOf(q)]];
      if (!hay.some((v) => (v || "").toLowerCase().includes(needle))) return false;
    }
    if (stageFilter !== "all" && stageOf(q) !== stageFilter) return false;
    if (sentFilter !== "all") {
      if (!q.sent_at) return false;
      if (new Date(q.sent_at) < sentSince(sentFilter)) return false;
    }
    return true;
  });
  const filtersActive = stageFilter !== "all" || sentFilter !== "all" || !!needle;

  const confirmSend = async () => {
    setSending(true);
    await sendQuote(confirmQ);
    setSending(false);
    setConfirmQ(null);
  };

  const [deleting, setDeleting] = useState(false);
  const [acceptQ, setAcceptQ] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const confirmAccept = async () => {
    setAccepting(true);
    try {
      await api.patch(`/quotes/${acceptQ.id}`, { status: "accepted" });
      toast.success(`${acceptQ.quote_number} accepted — deal won, install job & materials pipeline kicked off`);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setAcceptQ(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark accepted");
    }
    setAccepting(false);
  };
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/quotes/${delQ.id}`);
      toast.success(`Quote ${delQ.quote_number} deleted`);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setDelQ(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setDeleting(false);
  };

  return (
    <div>
      <PageHeader
        title="Quotes & Costings"
        subtitle="All quotes built with the costing calculator. Export and Xero integration coming soon."
        breadcrumb={["Sales", "Quotes"]}
        actions={
          <Button data-testid="new-quote-btn" onClick={() => nav("/quotes/new")} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New quote
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="quotes-filter-bar">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="quotes-search" value={qSearch} onChange={(e) => setQSearch(e.target.value)}
              placeholder="Search quote #, client, fence type..." className="pl-8 pr-7 h-9 rounded-sm w-72 text-sm" />
            {qSearch && (
              <button data-testid="quotes-search-clear" onClick={() => setQSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={13} />
              </button>
            )}
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="rounded-sm h-9 w-[180px] text-sm" data-testid="quotes-stage-filter">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => <SelectItem key={s} value={s} data-testid={`quotes-stage-opt-${s}`}>{STAGE_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sentFilter} onValueChange={setSentFilter}>
            <SelectTrigger className="rounded-sm h-9 w-[190px] text-sm" data-testid="quotes-sent-filter">
              <SelectValue placeholder="Sent — any time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sent — any time</SelectItem>
              {SENT_PERIODS.map((p) => <SelectItem key={p.v} value={p.v} data-testid={`quotes-sent-opt-${p.v}`}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {filtersActive && (
            <Button size="sm" variant="ghost" data-testid="quotes-clear-filters"
              onClick={() => { setStageFilter("all"); setSentFilter("all"); setQSearch(""); }}
              className="h-9 rounded-sm text-xs text-slate-500">
              <X size={13} className="mr-1" /> Clear
            </Button>
          )}
          <DefaultViewControl screen="quotes"
            current={{ stage: stageFilter, sent: sentFilter }}
            onApply={(f) => { if (f.stage) setStageFilter(f.stage); if (f.sent) setSentFilter(f.sent); }} />
          <span className="text-xs text-slate-400 ml-auto" data-testid="quotes-filter-count">
            {filtered.length} of {quotes.length} quotes{sentFilter !== "all" ? " · filtered by sent date" : ""}
          </span>
        </div>
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left stamp-label text-slate-500">
                <th className="px-4 py-3">Quote #</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Fence type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 hidden sm:table-cell">Viewed</th>
                <th className="px-4 py-3 text-right">Cost</th>
                <th className="px-4 py-3 text-right">GP%</th>
                <th className="px-4 py-3 text-right">Sale price</th>
                <th className="px-4 py-3 hidden lg:table-cell">Date</th>
                <th className="px-4 py-3 text-right">Send / PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((q) => (
                <tr key={q.id} className="hover:bg-cyan-50/30 cursor-pointer" onClick={() => nav(`/quotes/${q.id}`)} data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-slate-400" />
                      <span className="font-mono text-xs font-semibold text-slate-900">{hi(q.quote_number, needle)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-900 font-medium">{hi(q.customer_name, needle)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{hi(FENCE_LABELS[q.fence_type] || q.fence_type, needle)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_COLOR[stageOf(q)] || ""}`} data-testid={`quote-stage-${q.id}`}>{STAGE_LABEL[stageOf(q)] || q.status}</Badge></td>
                  <td className="px-4 py-3 hidden sm:table-cell" data-testid={`quote-views-${q.id}`}>
                    {q.portal_views ? (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${q.portal_views >= 3 ? "text-emerald-700" : "text-slate-700"}`}>
                        <Eye size={13} className={q.portal_views >= 3 ? "text-emerald-600" : "text-cyan-600"} />
                        {q.portal_views}×
                        <span className="text-slate-400 font-normal">{relTime(q.portal_last_viewed_at)}</span>
                      </span>
                    ) : q.status === "draft" ? (
                      <span className="text-[11px] text-slate-300">—</span>
                    ) : (
                      <span className="text-[11px] text-slate-400">Not opened</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtCurrency(q.total_cost)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{q.gp_margin_percent?.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-cyan-600">{fmtCurrency(q.sale_price)}</td>
                  <td className="px-4 py-3 text-slate-500 hidden lg:table-cell text-xs">{fmtDate(q.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {q.status !== "accepted" && q.status !== "declined" && (
                      <Button size="sm" variant="ghost" data-testid={`quote-accept-${q.id}`}
                        onClick={(e) => { e.stopPropagation(); setAcceptQ(q); }}
                        disabled={stageOf(q) !== "sent"}
                        title={stageOf(q) === "sent" ? "Mark as accepted (client accepted verbally / by email)" : "Send the quote to the client before marking it accepted"}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-600 disabled:opacity-30">
                        <CheckCircle2 size={15} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`quote-copy-${q.id}`}
                      onClick={(e) => { e.stopPropagation(); setDupQ(q); }}
                      title="Copy to a new quote"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-600">
                      <Copy size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`quote-send-${q.id}`}
                      onClick={(e) => { e.stopPropagation(); setConfirmQ(q); }}
                      title={!["approved", "sent", "accepted"].includes(stageOf(q)) ? "Approve the quote before emailing it to the client" : q.customer_email ? `Email to ${q.customer_email}` : "No customer email on quote"}
                      disabled={!q.customer_email || !["approved", "sent", "accepted"].includes(stageOf(q))}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-600 disabled:opacity-30">
                      <Mail size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`quote-pdf-${q.id}`}
                      onClick={(e) => { e.stopPropagation(); downloadPdf(q); }}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-600">
                      <FileDown size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`quote-delete-${q.id}`}
                      onClick={(e) => { e.stopPropagation(); setDelQ(q); }}
                      title="Delete quote"
                      className="h-8 w-8 p-0 text-slate-300 hover:text-rose-600">
                      <Trash2 size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
              {quotes.length === 0 && <tr><td colSpan={10} className="px-4 py-16 text-center text-slate-400">No quotes yet. <button className="text-cyan-600 font-semibold hover:underline" onClick={() => nav("/quotes/new")}>Create the first one →</button></td></tr>}
              {quotes.length > 0 && filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-slate-400" data-testid="quotes-no-match">
                  No quotes match these filters. <button className="text-cyan-600 font-semibold hover:underline" onClick={() => { setStageFilter("all"); setSentFilter("all"); }}>Clear filters</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <ConfirmSendQuoteDialog
        open={!!confirmQ}
        quote={confirmQ}
        onClose={() => setConfirmQ(null)}
        onSent={() => qc.invalidateQueries({ queryKey: ["quotes"] })}
      />

      <DuplicateQuoteDialog quote={dupQ} open={!!dupQ} onClose={() => setDupQ(null)} />

      {/* Accept quote confirm */}
      <Dialog open={!!acceptQ} onOpenChange={(o) => !o && setAcceptQ(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="accept-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" /> Mark {acceptQ?.quote_number} as accepted?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p className="text-slate-600"><b>{acceptQ?.customer_name}</b> · {fmtCurrency(acceptQ?.sale_price)} — use this when the client accepted verbally / by email instead of via the portal.</p>
            <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-xs text-emerald-900 space-y-1">
              <div>This will automatically:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Move the linked deal to <b>Won</b></li>
                <li>Create the <b>install job</b> with deposit tracking</li>
                <li>Generate the <b>material take-off</b>, blueprints &amp; fabrication items</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAcceptQ(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-accept-quote-btn" onClick={confirmAccept} disabled={accepting} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">
              {accepting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Yes — mark accepted
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete quote confirm */}
      <Dialog open={!!delQ} onOpenChange={(o) => !o && setDelQ(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="delete-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-500" /> Delete quote {delQ?.quote_number}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-1">
            <p><b>{delQ?.customer_name}</b> · {fmtCurrency(delQ?.sale_price)} · status <b>{delQ?.status}</b></p>
            <p className="text-xs text-slate-400">This permanently removes the quote. Any client portal link for it stops working. Cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelQ(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-delete-quote-btn" onClick={confirmDelete} disabled={deleting} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              {deleting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
              Delete quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
